import { observable, computed, action } from "mobx";
import { ObservableSubscription } from "@apollo/client";
import { store } from "@store";
import {
  GetTicketComments,
  UpdateTicketComment,
  DeleteTicketComment,
  AddTicketComment,
  AddTicketCommentWithReminder,
  WithTodoInput,
  TicketEventFragment
} from "@graphql";
import { ObservableGraphqlQuery } from "@clientUtils";

export default class TicketEventLogStore {
  @observable _ticketId: string;

  private _query = new ObservableGraphqlQuery(() =>
    GetTicketComments.funcWatch(
      { todoId: this.ticketId },
      { fetchPolicy: "network-only" } // or cacheRedirects is needed
    )
  );

  private subscription: ObservableSubscription;

  constructor(private ticketId: string) {
    this._ticketId = ticketId;
  }

  unsubscribe() {
    this.subscription && this.subscription.unsubscribe();
  }

  refetch = () => {
    this._query.refetch({ todoId: this._ticketId });
  };

  @computed get eventsSorted(): TicketEventFragment[] {
    if (!this._query.data?.todoEvents) {
      return [];
    }

    return this._query.data.todoEvents
      .sortBy((x) => !x.Sticky)
      .filter((ev) => !ev.Deleted);
  }

  @computed get error() {
    return this._query.error;
  }

  @computed get loading() {
    return this._query.loading;
  }

  @action.bound addTicketComment(desc: string) {
    return AddTicketComment.func({ id: this._ticketId, desc })
      .then(() => this.refetch())
      .catch((e) => {
        store().snackbars.error(`Cannot add comment.\nReason: ${e}`);
        console.error(e);
      });
  }

  @action.bound addTicketCommentWithReminder(desc: string, withTodoInput: WithTodoInput) {
    return AddTicketCommentWithReminder.func({
      id: this._ticketId,
      desc,
      withTodo: withTodoInput
    })
      .then(() => this.refetch())
      .catch((e) => {
        store().snackbars.error(`Cannot add comment.\nReason: ${e}`);
        console.error(e);
      });
  }

  deleteEvent = (id: string) => {
    DeleteTicketComment.func({ id })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot delete event.\nReason: ${err}`);
      });
  };

  updateEvent = (id: string, desc: string) => {
    return UpdateTicketComment.func({ id, desc })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot update event.\nReason: ${err}`);
      });
  };
}
